import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Shared 3-option picker for the live video aspect ratio.
 * Used by:
 *   - CreateLiveDialog (scheduling form) — kept inline, NOT refactored to avoid regressions.
 *   - LiveDashboardPage (Live Now flow — Feb 6 2026 bug fix).
 *
 * The visual treatment (small filled rectangle preview + label + amber active
 * state) MIRRORS the scheduling form so creators see identical controls in
 * both places.
 */
const LiveFormatPicker = ({ value, onChange, disabled = false, testIdPrefix = 'live-now-format', compact = false }) => {
  const { t } = useTranslation('lives');
  const OPTIONS = [
    { v: '16:9', label: t('format_picker.horizontal'), box: 'w-10 h-5' },
    { v: '1:1',  label: t('format_picker.square'),     box: 'w-7 h-7' },
    { v: '9:16', label: t('format_picker.vertical'),   box: 'w-5 h-8' },
  ];
  return (
    <div data-testid={`${testIdPrefix}-block`}>
      <label className="mb-1 block text-xs font-semibold text-foreground">{t('format_picker.label')}</label>
      <div className="flex gap-2">
        {OPTIONS.map((opt) => {
          const active = value === opt.v;
          return (
            <button
              key={opt.v}
              type="button"
              disabled={disabled}
              onClick={() => onChange(opt.v)}
              className={`flex ${compact ? 'min-w-[80px]' : 'flex-1'} flex-col items-center justify-center gap-1 rounded-md border px-2 py-2 text-[11px] font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${
                active
                  ? 'border-amber-500 bg-amber-500/10 text-amber-700'
                  : 'border-border bg-card text-foreground hover:bg-secondary'
              }`}
              data-testid={`${testIdPrefix}-${opt.v.replace(':', '-')}`}
              aria-pressed={active}
            >
              <div className={`rounded-sm bg-current opacity-40 ${opt.box}`} />
              {opt.label}
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-[10px] text-muted-foreground">
        {t('format_picker.hint')}
      </p>
    </div>
  );
};

export default LiveFormatPicker;
